import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function OTPLogin() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("+91");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      // Only shown when server runs in DEV_MODE=true. In production the code
      // must be delivered out-of-band (SMS/WhatsApp).
      setDevCode(data.dev_code || null);
      setStep("otp");
      toast.success(`OTP sent to ${phone}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send OTP");
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code: otp, name });
      localStorage.setItem("aarogya_token", data.token);
      await refresh();
      toast.success(`Welcome, ${data.user.name}`);
      nav(data.user.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Wrong OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-bone">
      <div className="hidden md:flex relative bg-ayurveda text-bone p-12 flex-col justify-between grain">
        <Link to="/" className="font-heritage text-3xl">Gutvibe</Link>
        <div>
          <div className="overline text-terracotta mb-4">Mobile OTP</div>
          <h1 className="font-heritage text-5xl leading-tight">Sign in with<br/>your phone.</h1>
          <p className="mt-6 text-bone/70 max-w-md">Voice-first, mobile-first. We'll send a 6-digit code to your phone.</p>
        </div>
        <div className="text-xs text-bone/50">Dev mode: code shown on-screen for demo.</div>
      </div>

      <div className="flex items-center justify-center p-8">
        {step === "phone" && (
          <motion.form onSubmit={requestOtp} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-5" data-testid="otp-phone-form">
            <div>
              <h2 className="font-heritage text-4xl text-ayurveda">Enter your phone</h2>
              <p className="text-sm text-charcoal/60 mt-2">Include country code (e.g. +91).</p>
            </div>
            <div className="space-y-2">
              <Label>Phone number</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" required data-testid="otp-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Your name (first-time only)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" data-testid="otp-name-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-ayurveda text-bone h-11" data-testid="otp-send-btn">
              {loading ? "Sending…" : "Send OTP"}
            </Button>
            <div className="text-sm text-charcoal/70 text-center">
              Prefer password? <Link to="/login" className="text-ayurveda font-medium underline underline-offset-4" data-testid="link-password-login">Email login</Link>
            </div>
          </motion.form>
        )}

        {step === "otp" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-5">
            <div>
              <h2 className="font-heritage text-4xl text-ayurveda">Enter the code</h2>
              <p className="text-sm text-charcoal/60 mt-2">Sent to {phone}. Valid for 5 minutes.</p>
            </div>
            {devCode && (
              <div className="rounded-lg bg-terracotta/10 border border-terracotta/30 p-3 text-sm">
                <div className="overline text-terracotta text-[10px] mb-1">Dev mode</div>
                <div className="font-clinical text-charcoal">Your code: <span className="font-bold">{devCode}</span></div>
              </div>
            )}
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-code-input">
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={verify} disabled={loading || otp.length !== 6} className="w-full bg-ayurveda text-bone h-11" data-testid="otp-verify-btn">
              {loading ? "Verifying…" : "Verify & continue"}
            </Button>
            <Button variant="ghost" onClick={() => { setStep("phone"); setOtp(""); setDevCode(null); }} className="w-full" data-testid="otp-back-btn">
              Change phone
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
